import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { downloadFile, formatDate, formatNumber } from '../../core/helpers';
import { normalizeDateFE } from '../../core/normalization';
import { ReactComponent as TipIcon } from '../../icons/tip.svg';
import Modal from '../Modal/Modal';
import './PaymentInstructionModal.scss';
import Comment from '../Comment';
import SellerFillingFile from '../../pages/Seller/SellerFiling/SellerFillingFile';
import { generatePaymentInstructionFileLink } from '../../core/api';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { DEFAULT_ERROR } from '../Notifications/Notification';
import { addNotification } from '../../core/store/notificationsSlice';
import Button from '../Button';
import { SellerFilingReportToConfirm } from '../../core/types';

interface Props {
  isModalOpened: boolean;
  closeModal: () => void;
  onClickConfirm: () => void;
  report: SellerFilingReportToConfirm | undefined;
  isConfirming: boolean;
}

export default function PaymentInstructionModal({
  isModalOpened,
  closeModal,
  report,
  onClickConfirm,
  isConfirming,
}: Props) {
  const activeCompany = useAppSelector((s) => s.user.activeCompany);
  const dispatch = useAppDispatch();

  const [confirmation, setConfirmation] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (isModalOpened) {
      setConfirmation(false);
    }
  }, [isModalOpened]);

  const instruction = report?.payment_instruction;

  if (!instruction || !report) return null;

  const period =
    report.period.type === 'month'
      ? formatDate(normalizeDateFE(report.period.from_date), 'MMMM yyyy')
      : formatDate(normalizeDateFE(report.period.from_date), 'QQQ yyyy');

  const date = normalizeDateFE(report.deadline_date);
  const paid = instruction?.confirmed;

  const isNegativeAmount = instruction.amount.amount < 0;
  const isZeroAmount = instruction.amount.amount === 0;

  const isDE = report.country.short_name === 'DE';

  const amount = (
    <>
      {formatNumber(instruction.amount.amount / 100, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}
      &nbsp;
      {instruction.amount.currency}
    </>
  );

  const onMarkAsPaidClick = () => {
    setConfirmation(true);
  };

  const closeBtn = (
    <Button
      styling='outline'
      type='button'
      onClick={closeModal}
      data-testid='payment-instruction-modal-close-button'
    >
      {t('Close')}
    </Button>
  );

  const periodRow = (
    <div className='row mb-3'>
      <div className='col-4'>{t('Period')}</div>
      <div className='col-8' data-testid='payment-instruction-modal-period'>
        {period}
      </div>
    </div>
  );

  const onDowloadFile =
    (setIsLoading: React.Dispatch<React.SetStateAction<boolean>>) => () => {
      setIsLoading(true);
      generatePaymentInstructionFileLink(
        instruction.id,
        activeCompany?.company_id
      )
        .then((res) => {
          setIsLoading(false);
          downloadFile(res, instruction.file!);
        })
        .catch(() => {
          setIsLoading(false);
          dispatch(
            addNotification({
              text: t(DEFAULT_ERROR),
              type: 'error',
            })
          );
        });
    };

  const renderInstruction = () => {
    if (instruction.is_sepa) {
      return (
        <>
          {periodRow}
          <div className='mb-3'>
            {t(
              'As you have set up the SEPA Direct Debit scheme with the French tax office, the payment due should be automatically debited from your bank account by the tax office.'
            )}
          </div>
          {closeBtn}
        </>
      );
    }
    if ((instruction.is_null_report || isZeroAmount) && isDE) {
      return (
        <>
          {periodRow}
          <div className='row align-items-center mb-3'>
            <div className='col-4'>{t('Amount')}</div>
            <div
              className='col-8'
              data-testid='payment-instruction-modal-amount'
            >
              {amount}
            </div>
          </div>
          <div className='mb-3'>
            {t('There is no VAT due from the VAT statement *date*.').replace(
              '*date*',
              formatDate(new Date(), 'MM/yyyy')
            )}
          </div>
          {closeBtn}
        </>
      );
    }
    if (instruction.is_null_report || isZeroAmount) {
      return (
        <>
          {periodRow}
          <div className='row align-items-center mb-3'>
            <div className='col-4'>{t('Amount')}</div>
            <div
              className='col-8'
              data-testid='payment-instruction-modal-amount'
            >
              {amount}
            </div>
          </div>
          <div className='mb-3'>{t('No payment needed.')}</div>
          {closeBtn}
        </>
      );
    }
    return (
      <>
        {periodRow}
        {!instruction.is_sepa &&
          !instruction.is_null_report &&
          !isNegativeAmount && (
            <>
              <div className='row mb-3'>
                <div className='col-4'>{t('Payee')}</div>
                <div
                  className='col-8 multiline'
                  data-testid='payment-instruction-modal-payee'
                >
                  {instruction.payee}
                </div>
              </div>
              <div className='row mb-3'>
                <div className='col-4'>{t('Credit institution')}</div>
                <div
                  className='col-8 multiline'
                  data-testid='payment-instruction-modal-credit-institution'
                >
                  {instruction.credit_institution}
                </div>
              </div>
              <div className='row mb-3'>
                <div className='col-4'>{t('IBAN')}</div>
                <div
                  className='col-8'
                  data-testid='payment-instruction-modal-iban'
                >
                  {instruction.iban}
                </div>
              </div>
              <div className='row mb-3'>
                <div className='col-4'>{t('BIC')}</div>
                <div
                  className='col-8'
                  data-testid='payment-instruction-modal-bic'
                >
                  {instruction.bic}
                </div>
              </div>
            </>
          )}
        <div className='row mb-3'>
          <div className='col-4'>{t('Country')}</div>
          <div
            className='col-8'
            data-testid='payment-instruction-modal-country'
          >
            {t(report.country.short_name)}
          </div>
        </div>
        {!isNegativeAmount && (
          <div className='row align-items-center mb-3'>
            <div className='col-4'>{t('Due Date')}</div>
            <div
              className='col-8'
              data-testid='payment-instruction-modal-due-date'
            >
              {formatDate(normalizeDateFE(instruction.due_date), 'dd.MM.yyyy')}
            </div>
          </div>
        )}
        {!instruction.is_sepa &&
          !instruction.is_null_report &&
          !isNegativeAmount && (
            <div className='row align-items-center mb-3'>
              <div className='col-4'>{t('Purpose')}</div>
              <div
                className='col-8'
                data-testid='payment-instruction-modal-purpose'
              >
                {instruction.purpose}
              </div>
            </div>
          )}
        <div className='row align-items-center mb-3'>
          <div className='col-4'>{t('Amount')}</div>
          <div className='col-8' data-testid='payment-instruction-modal-amount'>
            {amount}
          </div>
        </div>
        {instruction.comment && (
          <Comment comment={instruction.comment} className='mb-3' />
        )}
        {instruction.file && (
          <SellerFillingFile
            key={instruction.file}
            filename={instruction.file}
            className='mb-3'
            onDownload={onDowloadFile}
          />
        )}
        <div className='row row-cols-auto gx-2 mt-2'>
          {paid ? (
            <div className='col flex-shrink-0'>{closeBtn}</div>
          ) : (
            <>
              <div className='col flex-shrink-0'>
                <Button
                  data-testid='payment-instruction-modal-submit-btn'
                  onClick={onMarkAsPaidClick}
                >
                  {t('Mark as paid')}
                </Button>
              </div>
              <div className='col flex-shrink-0'>{closeBtn}</div>
            </>
          )}
        </div>
      </>
    );
  };

  return (
    <Modal
      isOpened={isModalOpened}
      close={closeModal}
      title={t('Payment instruction')}
      isLoading={isConfirming}
      name='PaymentInstruction'
    >
      {confirmation ? (
        <div
          className='d-flex align-items-center justify-content-center flex-column mb-3'
          style={{
            minHeight: 450,
          }}
        >
          <div className='Text--20 fw-800'>{t('Confirm payment')}</div>
          <div className='Text--20 mb-3'>{amount}</div>
          <Button
            data-testid='payment-instruction-modal-confirm-btn'
            onClick={onClickConfirm}
            disabled={isConfirming}
          >
            {t('Confirm')}
          </Button>
        </div>
      ) : (
        <div className='mx-3 pt-3 pb-4 px-3'>
          <div className='d-flex align-items-center mb-2'>
            <div className='Text--20 fw-800'>
              {formatDate(date, 'MMMM yyyy')}
            </div>
            {paid && !instruction.is_sepa && !instruction.is_null_report && (
              <div className='ms-auto Text--green fw-600 d-flex align-items-center'>
                {t('Paid')}&nbsp;
                <TipIcon className='PaymentInstructionModal__icon' />
              </div>
            )}
          </div>
          {renderInstruction()}
        </div>
      )}
    </Modal>
  );
}
